import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { ECHOPAY_SVG } from "@/assets/svgs";

export type ToastType = "success" | "info" | "warning" | "error";

export interface ToastProps {
  id?: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
  onClose?: () => void;
  position?:
    | "top-right"
    | "top-left"
    | "top-center"
    | "bottom-right"
    | "bottom-left"
    | "bottom-center";
}

interface ToastConfig {
  bgColor: string;
  borderColor: string;
  textColor: string;
  icon: React.ReactNode;
  shadowColor: string;
}

const Toast: React.FC<ToastProps> = ({
  type,
  title,
  message,
  duration = 5000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(true);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const remainingTimeRef = useRef<number>(duration);

  /* ------------------ TIMER LOGIC ------------------ */
  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(handleClose, remainingTimeRef.current);
  };

  const pauseTimer = () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;

    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current -= elapsed;
  };

  const resumeTimer = () => {
    if (remainingTimeRef.current > 0) {
      startTimer();
    }
  };

  useEffect(() => {
    startTimer();

    // Trigger slide-in animation
    requestAnimationFrame(() => setIsEntering(false));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  /* ------------------ CLOSE HANDLER ------------------ */
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  };

  if (!isVisible) return null;

  /* ------------------ STYLES ------------------ */
  const toastConfigs: Record<ToastType, ToastConfig> = {
    success: {
      bgColor: "bg-[#9AE45F1A]",
      borderColor: "border-[#9AE45F]",
      textColor: "text-[#010721]",
      icon: ECHOPAY_SVG().toastSuccess(),
      shadowColor: "shadow-[#00A63E38]",
    },
    info: {
      bgColor: "bg-[#5FB5E41A]",
      borderColor: "border-[#009DFF]",
      textColor: "text-[#010721]",
      icon: ECHOPAY_SVG().toastInfo(),
      shadowColor: "shadow-[#5FB5E438]",
    },
    warning: {
      bgColor: "bg-[#E4D75F1A]",
      borderColor: "border-[#FFAE00]",
      textColor: "text-[#010721]",
      icon: ECHOPAY_SVG().toastWarning(),
      shadowColor: "shadow-[#FECF7738]",
    },
    error: {
      bgColor: "bg-[#E4755F1A]",
      borderColor: "border-[#FF4400]",
      textColor: "text-[#010721]",
      icon: ECHOPAY_SVG().toastError(),
      shadowColor: "shadow-[#E4755F38]",
    },
  };

  const config = toastConfigs[type];

  return (
    <div
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
      role="alert"
      aria-live="polite"
      className={`
        flex items-start gap-2 sm:gap-4 p-4 rounded-xl border shadow-lg backdrop-blur ${
          config.shadowColor
        }
        min-w-[300px] max-w-[90vw] sm:max-w-md
        ${config.bgColor} ${config.borderColor}
        transition-all duration-300 ease-in-out
        ${
          isEntering
            ? "opacity-0 translate-x-8"
            : isExiting
            ? "opacity-0 translate-x-full"
            : "opacity-100 translate-x-0"
        }
      `}
    >
      {/* Icon */}
      <div
        className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center`}
      >
        {config.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3
          className={`font-semibold text-base sm:text-lg ${config.textColor} mb-1`}
        >
          {title}
        </h3>
        <p className={`text-sm sm:text-base ${config.textColor} opacity-90`}>
          {message}
        </p>
      </div>

      {/* Close Button */}
      <button
        onClick={handleClose}
        className={`flex-shrink-0 ${config.textColor} opacity-70 hover:opacity-100 transition-opacity`}
        aria-label="Close notification"
      >
        <X className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
    </div>
  );
};

export default Toast;
